import { ImprovementData, ImprovementGoal, ImprovementSurvey, OrgGoal, ScoresObject, Stats, Survey } from "../../types";
import { scoreResult2ScoresObject } from "../../lib/mapping";

export type SurveyRow = {
  id: string;
  surveyName: string;
  changed?: string;
  respondents?: number;
  isBaseline: boolean;
  isOpen: boolean;
  scores: Record<string, number | undefined>;
  diff: Record<string, number | undefined>;
};

type RowsInput = {
  goal: Pick<ImprovementGoal, "originalSurvey" | "categories">;
  improvementData?: ImprovementData | null;
  originalSurvey?: Survey | null;
  recentSurveys?: ImprovementSurvey[] | null;
};

const pick = (scores: ScoresObject | null | undefined, categories: string[]) =>
  categories.reduce<Record<string, number | undefined>>((acc, c) => {
    const value = scores?.[c as keyof ScoresObject];
    acc[c] = typeof value === "number" ? value : undefined;
    return acc;
  }, {});

/**
 * Rows for the "Latest surveys" table: the original survey first as the baseline, then the
 * surveys created after the goal started, oldest first. Scores only for the goal's categories.
 */
export const surveyRows = ({ goal, improvementData, originalSurvey, recentSurveys }: RowsInput): SurveyRow[] => {
  const categories = goal.categories.filter(Boolean);
  const baseScores = pick(improvementData, categories);
  const diffOf = (scores: Record<string, number | undefined>) =>
    categories.reduce<Record<string, number | undefined>>((acc, c) => {
      const value = scores[c];
      const base = baseScores[c];
      acc[c] = value && base ? value - base : undefined;
      return acc;
    }, {});

  const baseline: SurveyRow = {
    id: goal.originalSurvey,
    surveyName: improvementData?.surveyName || originalSurvey?.surveyName || "",
    changed: originalSurvey?.changed ? `${originalSurvey.changed}` : undefined,
    respondents: originalSurvey?.respondents,
    isBaseline: true,
    isOpen: originalSurvey?.status === "ongoing",
    scores: baseScores,
    diff: {},
  };

  const recent = [...(recentSurveys || [])]
    .sort((a, b) => (a.startDate < b.startDate ? -1 : a.startDate > b.startDate ? 1 : 0))
    .map((s) => {
      const scores = pick(scoreResult2ScoresObject(s.answers || []), categories);
      return {
        id: s.id,
        surveyName: s.surveyName,
        changed: s.changed,
        respondents: s.respondents,
        isBaseline: false,
        isOpen: s.status === "ongoing",
        scores,
        diff: diffOf(scores),
      };
    });

  return [baseline, ...recent];
};

type OrgRowsInput = {
  goal: Pick<OrgGoal, "categories" | "scoreData" | "originalTo">;
  recentStats?: Stats | null;
  /** Row names, already translated, so the function needs no i18n. */
  labels: { baseline: string; recent: string };
};

/**
 * Rows for an organization goal: the original period as the baseline, then the goal period from
 * /organization/stats. Without answers in the goal period only the baseline is given.
 */
export const orgRows = ({ goal, recentStats, labels }: OrgRowsInput): SurveyRow[] => {
  const categories = goal.categories.filter(Boolean);
  const baseScores = pick(goal.scoreData, categories);
  const baseline: SurveyRow = {
    id: "baseline",
    surveyName: labels.baseline,
    changed: goal.originalTo,
    isBaseline: true,
    isOpen: false,
    scores: baseScores,
    diff: {},
  };
  if (!recentStats || recentStats.total.surveys === 0) {
    return [baseline];
  }
  const scores = pick(scoreResult2ScoresObject(recentStats.questions || []), categories);
  const diff = categories.reduce<Record<string, number | undefined>>((acc, c) => {
    const value = scores[c];
    const base = baseScores[c];
    acc[c] = value && base ? value - base : undefined;
    return acc;
  }, {});
  return [
    baseline,
    {
      id: "recent",
      surveyName: labels.recent,
      changed: recentStats.total.endTime,
      respondents: recentStats.total.answers,
      isBaseline: false,
      isOpen: false,
      scores,
      diff,
    },
  ];
};

export type MovementSummary = {
  key: "noRecentSuveys" | "movementSummary";
  up: string[];
  down: string[];
  same: string[];
  openCount: number;
};

/** Compares the latest survey with scores against the baseline, per category. */
export const movementSummary = (rows: SurveyRow[], categories: string[]): MovementSummary => {
  const recent = rows.filter((r) => !r.isBaseline);
  const openCount = recent.filter((r) => r.isOpen).length;
  const latest = [...recent].reverse().find((r) => categories.some((c) => r.scores[c] !== undefined));
  const summary: MovementSummary = { key: "movementSummary", up: [], down: [], same: [], openCount };
  if (!latest) {
    return { ...summary, key: "noRecentSuveys" };
  }
  categories.forEach((c) => {
    const diff = latest.diff[c];
    if (diff === undefined) {
      return;
    }
    if (diff > 0.05) {
      summary.up.push(c);
    } else if (diff < -0.05) {
      summary.down.push(c);
    } else {
      summary.same.push(c);
    }
  });
  return summary;
};
